import { Component, Watch, mixins, namespace } from 'nuxt-property-decorator'
import { Route } from 'vue-router'
import { IProductsRaw } from '~/assets/interfaces/products'
import MixinGeneral from '~/assets/mixins/general'
import { runIdle } from '~/assets/functions/runIdle'
import { checkForNoYAxisOffset } from '~/assets/functions/check-for-no-y-axis-offset'

const siteStore = namespace('site')
const catalogStore = namespace('catalog')

// Возврат из карточки товара в список: страница каталога запоминает список и
// позицию перед уходом (rememberCatalogForReturn), а вернувшись, показывает
// тот же список и прокручивает к карточке, из которой ушли. Карточка
// помечается в шаблоне: :ref="isProductForScroll(item) ? 'productForScrollRef' : null".
@Component
export default class MixinCatalogScrollRestore extends mixins(MixinGeneral) {
  @siteStore.Getter('getFixedHeaderHeight')
  public getFixedHeaderHeight!: number | null

  @catalogStore.State('backFromProductName')
  public backFromProductName!: string | null

  @catalogStore.State('catalogUrlBeforeLeave')
  public catalogUrlBeforeLeave!: string | null

  @catalogStore.State('scrollYBeforeLeave')
  public scrollYBeforeLeave!: number | null

  @catalogStore.Getter('getProducts')
  public savedProducts!: IProductsRaw | null

  @catalogStore.Mutation('setBackFromProductName')
  public setBackFromProductName!: (val: string | null) => void

  @catalogStore.Mutation('setBackFromCatalogName')
  public setBackFromCatalogName!: (val: string | null) => void

  @catalogStore.Mutation('setCatalogUrlBeforeLeave')
  public setCatalogUrlBeforeLeave!: (val: string | null) => void

  @catalogStore.Mutation('setScrollYBeforeLeave')
  public setScrollYBeforeLeave!: (val: number | null) => void

  @catalogStore.Mutation('setProducts')
  public setSavedProducts!: (val: IProductsRaw | null) => void

  // Список товаров страницы — его страница заполняет сама.
  public products: IProductsRaw | null = null

  public productForScroll: string | null = null

  private get scrollOffset(): number {
    return this.isMobile ? -16 : -32
  }

  @Watch('backFromProductName', { immediate: true })
  private backFromProductNameHandler(value: string | null) {
    if (
      value &&
      this.products &&
      this.catalogUrlBeforeLeave === this.$route.fullPath
    ) {
      // Копия: список на странице меняется (подгрузка «показать ещё»),
      // а состояние стора править вне мутации нельзя.
      if (this.savedProducts) {
        this.products = JSON.parse(JSON.stringify(this.savedProducts))
      }
      // Карточка, из которой ушли: к ней возвращаем экран после отрисовки.
      this.productForScroll = value
    }
  }

  // Сравниваем последним сегментом url, а не includes: слаг одного товара
  // бывает началом слага другого («..._atlant» и «..._atlant_2»), и подстрока
  // подсветила бы обе карточки.
  public isProductForScroll(item: { url?: string }): boolean {
    if (!this.productForScroll || !item.url) return false

    const slug = item.url.split('/').filter(Boolean).pop()

    return slug === this.productForScroll
  }

  public mounted() {
    if (this.productForScroll) {
      this.restoreScrollToProduct()
    }
  }

  // Список товаров лежит под LazyHydrate when-idle, поэтому ref появляется не
  // сразу — ждём простоя и следующего тика, иначе прокручивать нечего.
  private restoreScrollToProduct() {
    runIdle(() => {
      this.$nextTick(async () => {
        const refs = this.$refs.productForScrollRef as HTMLElement[] | undefined
        const el = refs && refs[0]

        if (el) {
          // Выше карточки догружаются картинки соседей и двигают её вниз —
          // прокрутка до того, как вёрстка встанет, промахнётся мимо.
          await checkForNoYAxisOffset(el)

          window.scrollTo({
            top:
              window.pageYOffset +
              el.getBoundingClientRect().top +
              this.scrollOffset -
              (this.getFixedHeaderHeight || 0),
            // Именно auto: возврат назад должен выглядеть как «страница уже
            // была тут», а не как проматывание сверху вниз на глазах.
            behavior: 'auto',
          })
        } else if (this.scrollYBeforeLeave !== null) {
          // Карточки в списке нет — ушли из блока под ним («Ликвидация
          // склада» и подобных): возвращаемся по сохранённой позиции.
          await this.restoreScrollToY(this.scrollYBeforeLeave)
        }

        this.productForScroll = null
      })
    })
  }

  // Блоки под списком грузятся уже после mounted, и в момент возврата
  // документ ещё короче нужной позиции — ждём, пока он дорастёт.
  private restoreScrollToY(y: number): Promise<void> {
    return new Promise((resolve) => {
      const step = 100
      const maxWait = 3000
      let waited = 0

      const tryScroll = () => {
        const docHeight = document.documentElement.scrollHeight
        const tallEnough = docHeight >= y + window.innerHeight

        if (tallEnough || waited >= maxWait) {
          window.scrollTo({ top: y, behavior: 'auto' })
          resolve()
          return
        }

        waited += step
        window.setTimeout(tryScroll, step)
      }

      tryScroll()
    })
  }

  // Вызывать из beforeRouteLeave: уходим в товар — запоминаем, куда вернуться.
  public rememberCatalogForReturn(to: Route, from: Route) {
    this.setSavedProducts(this.products)
    this.setBackFromProductName(to.params._id)
    this.setBackFromCatalogName(from.params._slug)
    this.setCatalogUrlBeforeLeave(from.fullPath)
    this.setScrollYBeforeLeave(window.scrollY)
  }

  public forgetCatalogForReturn() {
    this.setSavedProducts(null)
    this.setBackFromProductName(null)
    this.setBackFromCatalogName(null)
    this.setCatalogUrlBeforeLeave(null)
    this.setScrollYBeforeLeave(null)
  }
}
